/* eslint-disable max-lines */
import first from "lodash/first";
import { type FunctionComponent, useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { graphql, useFragment } from "react-relay";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";
import { Edition, useEditionValue } from "shared/lib/edition";
import { theme } from "shared/lib/styles";
import { isAfterOrEqual, isBeforeOrEqual, notEmpty } from "shared/lib/utils";

import { AdSlot } from "scmp-app/components/advertisement/ad-slots/ad-slot";
import { TargetingPaidType } from "scmp-app/components/advertisement/ad-slots/types";
import { HomeV2ClientQueryLoader } from "scmp-app/components/home-v2/client-query-loader";
import { HomeV2Newsletter } from "scmp-app/components/home-v2/newsletter";
import { HomeV2PlusWidget } from "scmp-app/components/home-v2/plus-widget";
import { type Section } from "scmp-app/components/home-v2/section-top";
import { HomeV2SuperSplash } from "scmp-app/components/home-v2/super-splash";
import { HomeV2TrendingTopic } from "scmp-app/components/home-v2/trending-topic";
import { usePlusWidgetConfig } from "scmp-app/components/plus/plus-widget/hooks";
import { AiEngineDataType } from "scmp-app/lib/ai-engine/enums";
import type { homePageQuery$data } from "scmp-app/queries/__generated__/homePageQuery.graphql";
import type { v2RecommendedForYouSponsorArticleQueueItemsEdge$key } from "scmp-app/queries/__generated__/v2RecommendedForYouSponsorArticleQueueItemsEdge.graphql";

import { usePickMorningStudioArticles, usePremiumNativeAd } from "./hooks";
import {
  AdvertisersWidget,
  AIWidget1,
  AIWidget2,
  AroundWidget,
  CFTWidget,
  Container,
  ContentContainer,
  ContentWrapper,
  DesktopBannerAdsBottom,
  DesktopBannerAdsTop,
  DesktopSidebarAds,
  Divider1,
  Divider2,
  Divider3,
  Divider4,
  FlexiUnitWidget,
  HeroArticle,
  HeroSecondaryArticles,
  LatestWidget,
  LatestWidgetAds,
  MobileFooterAds,
  MostPopularWidget,
  MostPopularWidgetAds,
  MultimediaWidget,
  MultimediaWidgetAds,
  NewsletterWidget,
  OpenQuestionWidget,
  OpinionWidget,
  OpinionWidgetAds,
  PremiumAds,
  SCMPShortsWidget,
  SectionBlock1,
  SectionBlock2,
  SectionBlock3,
  SectionBlock4,
  SectionBlock5,
  SeriesWidget,
  SpotlightWidget,
  StyledAppFooter,
  StyledContentItemHomeNativeArticle,
  StyledTakeoverAdSlot,
  SubscriptionWidget,
  ThingsToDoWidget,
  ThingsToDoWidgetAds,
  TopStories,
  TopStoriesAds,
} from "./styles";

type Props = {
  adZone: string;
  randomSeed?: string;
  reference: homePageQuery$data;
  // Entity ids already rendered by the SSR page, excluded from the AI engine
  // recommendations so the widgets don't repeat on-page content.
  ssrContentEntityIds: string[];
};

// Per-edition ordering of the first/second/fourth section blocks, same as v1
// (the third block is always china and the fifth always style).
const sectionBlocksByEdition: Record<
  Edition,
  { first: Section; fourth: Section; second: Section }
> = {
  [Edition.Asia]: { first: "twia", fourth: "lifestyle", second: "people-culture" },
  [Edition.HongKong]: { first: "hong-kong", fourth: "wellness", second: "lifestyle" },
  [Edition.International]: { first: "hong-kong", fourth: "people-culture", second: "lifestyle" },
};

export const HomeV2: FunctionComponent<Props> = ({
  adZone,
  randomSeed,
  reference,
  ssrContentEntityIds,
}) => {
  const {
    slot1: morningStudioArticleSlot1,
    slot2: morningStudioArticleSlot2,
    slot3: morningStudioArticleSlot3,
  } = usePickMorningStudioArticles(randomSeed ?? "fallback seed", reference.morningStudioQueue);
  const nativeAdsReference = first(
    [morningStudioArticleSlot1, morningStudioArticleSlot2].filter(notEmpty),
  );
  const recommendedForYouSponsorArticleReference =
    useFragment<v2RecommendedForYouSponsorArticleQueueItemsEdge$key>(
      graphql`
        fragment v2RecommendedForYouSponsorArticleQueueItemsEdge on QueueItemsEdge {
          node {
            ... on Content {
              ...homeNativeArticleContentItemContent
            }
          }
        }
      `,
      morningStudioArticleSlot3 ?? null,
    );

  // The Top Stories queue is shared with the hero widgets: edges[0] is the lead
  // hero article, edges[1..2] are the hero's secondary row, and edges[3..17]
  // (15 articles, given the query's `first: 18`) feed the Top Stories block in
  // order. The sponsored morning-studio article renders as a native-ad
  // side-card inside Top Stories (see HomeV2TopStories) rather than occupying
  // one of the article rows.
  const queueEdges = reference.topStories?.items?.edges?.filter(notEmpty) ?? [];
  const heroEdge = first(queueEdges);
  const heroSecondaryEdges = queueEdges.slice(1, 3);
  const topStoriesEdges = queueEdges.slice(3);

  const { plusWidgetConfig } = usePlusWidgetConfig(reference);
  // The highlight widget is subscriber-only (gated by `hasScmpPlusAccessRight`
  // inside HomeV2PlusWidget), so the relevant disable schedule is `withAccess`.
  const isPlusDisabled = useMemo(
    () =>
      plusWidgetConfig?.withAccess?.disabledDateRanges?.some(({ end, start }) => {
        const now = new Date();
        return isAfterOrEqual(now, start) && (notEmpty(end) ? isBeforeOrEqual(now, end) : true);
      }) ?? false,
    [plusWidgetConfig],
  );

  const isEnabledPremiumNativeAd = usePremiumNativeAd(reference);
  const edition = useEditionValue();

  return (
    <Container>
      <ContentContainer>
        <HomeV2SuperSplash reference={reference} />
        <ContentWrapper $edition={edition} $hasPremiumNativeAd={isEnabledPremiumNativeAd}>
          {notEmpty(heroEdge?.node) && <HeroArticle reference={heroEdge.node} />}
          {notEmpty(heroSecondaryEdges) && <HeroSecondaryArticles reference={heroSecondaryEdges} />}
          {notEmpty(topStoriesEdges) && (
            <TopStories
              reference={topStoriesEdges}
              slots={{
                mobileInlineAds: (
                  <AdSlot
                    adUnit="m_lrec1"
                    breakpoint={theme.breakpoints.only("mobile")}
                    sizes={[[300, 250]]}
                    targeting={{
                      paid: TargetingPaidType.NotArticle,
                    }}
                    withBackground
                    withLabel
                    zone={adZone}
                  />
                ),
                nativeAds: (
                  <>
                    <AdSlot
                      adUnit="d_native2c"
                      breakpoint={theme.breakpoints.up("desktop")}
                      sizes={[[1, 1], "fluid"]}
                      targeting={{ paid: TargetingPaidType.NotArticle }}
                      zone={adZone}
                    />
                    <AdSlot
                      adUnit="m_native2c"
                      breakpoint={theme.breakpoints.down("desktop")}
                      sizes={[[1, 1], "fluid"]}
                      targeting={{ paid: TargetingPaidType.NotArticle }}
                      zone={adZone}
                    />
                  </>
                ),
                plusWidget: !isPlusDisabled && <HomeV2PlusWidget reference={reference} />,
                sponsorArticle: (tracking, ref) =>
                  notEmpty(nativeAdsReference?.node) && (
                    <StyledContentItemHomeNativeArticle
                      onClick={(entityId, urlAlias) => {
                        tracking({
                          entityId,
                          index: 3,
                          isSponsorArticle: true,
                          urlAlias,
                        });
                      }}
                      ref={ref}
                      reference={nativeAdsReference.node}
                    />
                  ),
              }}
            />
          )}

          <ErrorBoundary fallback={<></>}>
            <NewsletterWidget>
              <HomeV2Newsletter />
            </NewsletterWidget>
          </ErrorBoundary>

          <FlexiUnitWidget reference={reference} />
          <OpinionWidget reference={reference} />
          <LatestWidget reference={reference} />
          <ThingsToDoWidget reference={reference} />

          <SeriesWidget reference={reference} />
          <OpenQuestionWidget reference={reference} />
          <CFTWidget reference={reference} />
          <SCMPShortsWidget reference={reference} />
          <SpotlightWidget reference={reference} />

          <TopStoriesAds>
            <AdSlot
              adUnit="m_banner1"
              breakpoint={theme.breakpoints.only("mobile")}
              sizes={[
                [300, 100],
                [320, 100],
                [300, 50],
                [320, 50],
                [300, 250],
              ]}
              targeting={{
                paid: TargetingPaidType.NotArticle,
              }}
              withBackground
              withLabel
              withLabelBackground
              zone={adZone}
            />
            <AdSlot
              adUnit="m_lrec1"
              breakpoint={theme.breakpoints.only("tablet")}
              sizes={[[300, 250]]}
              targeting={{
                paid: TargetingPaidType.NotArticle,
              }}
              withBackground
              withLabel
              withLabelBackground
              zone={adZone}
            />
            <AdSlot
              adUnit="d_lrec1"
              breakpoint={theme.breakpoints.up("desktop")}
              sizes={[[300, 250]]}
              targeting={{
                paid: TargetingPaidType.NotArticle,
              }}
              withLabel
              withLabelBackground
              zone={adZone}
            />
          </TopStoriesAds>

          {isEnabledPremiumNativeAd && (
            <PremiumAds>
              <AdSlot
                adUnit="m_native2f"
                breakpoint={theme.breakpoints.down("desktop")}
                sizes={[[1, 1], "fluid"]}
                targeting={{ paid: TargetingPaidType.NotArticle }}
                zone={adZone}
              />
              <AdSlot
                adUnit="d_native2f"
                breakpoint={theme.breakpoints.up("desktop")}
                sizes={[[1, 1], "fluid"]}
                targeting={{ paid: TargetingPaidType.NotArticle }}
                zone={adZone}
              />
            </PremiumAds>
          )}

          <OpinionWidgetAds>
            <AdSlot
              adUnit="m_lrec3"
              breakpoint={theme.breakpoints.down("desktop")}
              sizes={[
                [300, 250],
                [320, 480],
              ]}
              targeting={{
                paid: TargetingPaidType.NotArticle,
              }}
              withBackground
              withLabel
              withLabelBackground
              zone={adZone}
            />
            <AdSlot
              adUnit="d_lrec3"
              breakpoint={theme.breakpoints.up("desktop")}
              sizes={[
                [300, 250],
                [300, 600],
              ]}
              targeting={{
                paid: TargetingPaidType.NotArticle,
              }}
              withLabel
              withLabelBackground
              withMaxContainerSize={false}
              zone={adZone}
            />
          </OpinionWidgetAds>

          <DesktopSidebarAds>
            <AdSlot
              adUnit="d_lrec3a"
              breakpoint={theme.breakpoints.up("desktop")}
              sizes={[[300, 250]]}
              targeting={{
                paid: TargetingPaidType.NotArticle,
              }}
              withLabel
              withLabelBackground
              zone={adZone}
            />
          </DesktopSidebarAds>

          <LatestWidgetAds>
            <AdSlot
              adUnit="m_lrec3c"
              breakpoint={theme.breakpoints.down("desktop")}
              sizes={[
                [300, 250],
                [320, 480],
              ]}
              targeting={{
                paid: TargetingPaidType.NotArticle,
              }}
              withBackground
              withLabel
              withLabelBackground
              zone={adZone}
            />
            <AdSlot
              adUnit="d_lrec2"
              breakpoint={theme.breakpoints.up("desktop")}
              sizes={[
                [300, 250],
                [300, 600],
              ]}
              targeting={{
                paid: TargetingPaidType.NotArticle,
              }}
              withLabel
              withLabelBackground
              withMaxContainerSize={false}
              zone={adZone}
            />
          </LatestWidgetAds>

          <ThingsToDoWidgetAds>
            <AdSlot
              adUnit="m_lrec2"
              breakpoint={theme.breakpoints.down("desktop")}
              sizes={[
                [300, 250],
                [320, 480],
              ]}
              targeting={{
                paid: TargetingPaidType.NotArticle,
              }}
              withBackground
              withLabel
              withLabelBackground
              zone={adZone}
            />
            <AdSlot
              adUnit="d_lrec4"
              breakpoint={theme.breakpoints.up("desktop")}
              sizes={[[300, 250]]}
              targeting={{
                paid: TargetingPaidType.NotArticle,
              }}
              withLabel
              withLabelBackground
              zone={adZone}
            />
          </ThingsToDoWidgetAds>

          <DesktopBannerAdsTop>
            <AdSlot
              adUnit="d_banner2"
              breakpoint={theme.breakpoints.up("desktop")}
              sizes={[
                [970, 250],
                [728, 90],
                [970, 90],
              ]}
              targeting={{
                paid: TargetingPaidType.NotArticle,
              }}
              withBackground
              zone={adZone}
            />
          </DesktopBannerAdsTop>

          <DesktopBannerAdsBottom>
            <AdSlot
              adUnit="d_banner5"
              breakpoint={theme.breakpoints.up("desktop")}
              sizes={[
                [970, 250],
                [728, 90],
                [970, 90],
              ]}
              targeting={{
                paid: TargetingPaidType.NotArticle,
              }}
              withBackground
              zone={adZone}
            />
          </DesktopBannerAdsBottom>

          <Divider1 />
          <Divider2 />

          <ErrorBoundary fallback={<></>}>
            <ClientSideSuspense fallback={<></>}>
              <HomeV2ClientQueryLoader ssrContentEntityIds={ssrContentEntityIds}>
                {({ aiWidgets, data, existingEntityIds }) => {
                  const sectionBlocks = sectionBlocksByEdition[edition];
                  const advertisersReference = data.advertisers?.items;

                  const hasRecommendedForYou =
                    aiWidgets[AiEngineDataType.HomeRecommendedForYou].entityIds.length > 0;

                  const hasYouMayHaveMissed =
                    aiWidgets[AiEngineDataType.HomeYouMayHaveMissed].entityIds.length > 0;

                  return (
                    <>
                      {hasRecommendedForYou && (
                        <AIWidget1
                          aiEngineResponse={aiWidgets[AiEngineDataType.HomeRecommendedForYou]}
                          slots={{
                            sponsorArticle(tracking, ref) {
                              return (
                                <StyledContentItemHomeNativeArticle
                                  onClick={tracking}
                                  ref={ref}
                                  reference={recommendedForYouSponsorArticleReference.node}
                                />
                              );
                            },
                          }}
                          variant={AiEngineDataType.HomeRecommendedForYou}
                        />
                      )}
                      {hasYouMayHaveMissed && (
                        <AIWidget2
                          aiEngineResponse={aiWidgets[AiEngineDataType.HomeYouMayHaveMissed]}
                          slots={{
                            nativeAds: (
                              <>
                                <AdSlot
                                  adUnit="d_native2a"
                                  breakpoint={theme.breakpoints.up("desktop")}
                                  customizedAdUnitTargeting="dnative-6"
                                  sizes={[[1, 1], "fluid"]}
                                  targeting={{ paid: TargetingPaidType.NotArticle }}
                                  zone={adZone}
                                />
                                <AdSlot
                                  adUnit="m_native2a"
                                  breakpoint={theme.breakpoints.down("desktop")}
                                  customizedAdUnitTargeting="dnative-6"
                                  sizes={[[1, 1], "fluid"]}
                                  targeting={{ paid: TargetingPaidType.NotArticle }}
                                  zone={adZone}
                                />
                              </>
                            ),
                          }}
                          variant={AiEngineDataType.HomeYouMayHaveMissed}
                        />
                      )}
                      {advertisersReference && (
                        <AdvertisersWidget reference={advertisersReference} />
                      )}
                      <AroundWidget existingEntityIds={existingEntityIds} />
                      <MostPopularWidget
                        reference={data}
                        trendingTopicSlot={<HomeV2TrendingTopic reference={data} />}
                      />
                      <MultimediaWidget reference={data} />
                      <SubscriptionWidget />
                      <SectionBlock1 reference={data} section={sectionBlocks.first} />
                      <SectionBlock2 reference={data} section={sectionBlocks.second} />
                      <SectionBlock3 reference={data} section="china" />
                      <SectionBlock4 reference={data} section={sectionBlocks.fourth} />
                      <SectionBlock5 reference={data} section="style" />
                      <MultimediaWidgetAds>
                        <AdSlot
                          adUnit="m_lrec3g"
                          breakpoint={theme.breakpoints.down("desktop")}
                          sizes={[
                            [300, 250],
                            [320, 480],
                          ]}
                          targeting={{
                            paid: TargetingPaidType.NotArticle,
                          }}
                          withBackground
                          withLabel
                          withLabelBackground
                          zone={adZone}
                        />
                        <AdSlot
                          adUnit="d_lrec4a"
                          breakpoint={theme.breakpoints.up("desktop")}
                          sizes={[
                            [300, 250],
                            [300, 600],
                          ]}
                          targeting={{
                            paid: TargetingPaidType.NotArticle,
                          }}
                          withLabel
                          withLabelBackground
                          withMaxContainerSize={false}
                          zone={adZone}
                        />
                      </MultimediaWidgetAds>
                      <MostPopularWidgetAds>
                        <AdSlot
                          adUnit="m_lrec4"
                          breakpoint={theme.breakpoints.down("desktop")}
                          sizes={[
                            [300, 250],
                            [320, 480],
                          ]}
                          targeting={{
                            paid: TargetingPaidType.NotArticle,
                          }}
                          withBackground
                          withLabel
                          withLabelBackground
                          zone={adZone}
                        />
                        <AdSlot
                          adUnit="d_lrec4b"
                          breakpoint={theme.breakpoints.up("desktop")}
                          sizes={[
                            [300, 250],
                            [300, 600],
                          ]}
                          targeting={{
                            paid: TargetingPaidType.NotArticle,
                          }}
                          withLabel
                          withLabelBackground
                          withMaxContainerSize={false}
                          zone={adZone}
                        />
                      </MostPopularWidgetAds>
                      <MobileFooterAds>
                        <AdSlot
                          adUnit="m_lrec3h"
                          breakpoint={theme.breakpoints.only("mobile")}
                          sizes={[
                            [300, 250],
                            [320, 480],
                          ]}
                          targeting={{
                            paid: TargetingPaidType.NotArticle,
                          }}
                          withBackground
                          withLabel
                          withLabelBackground
                          zone={adZone}
                        />
                      </MobileFooterAds>
                      <Divider3 />
                      <Divider4 />
                    </>
                  );
                }}
              </HomeV2ClientQueryLoader>
              <StyledAppFooter />
            </ClientSideSuspense>
          </ErrorBoundary>
        </ContentWrapper>

        <StyledTakeoverAdSlot adSlotProps={{ zone: adZone }} variant="home" />
      </ContentContainer>
    </Container>
  );
};
